import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BookOpen, ChevronDown, ChevronRight, Copy, Check, Play, ExternalLink, Server, Shield, Zap, Pickaxe, Trees, Landmark, Leaf, Mountain, Layers } from "lucide-react";
import ModuleHeader from "@/components/ModuleHeader";

const BASE_URL = typeof window !== "undefined" ? window.location.origin : "";

type Endpoint = {
  method: "GET" | "POST" | "PUT" | "DELETE";
  path: string;
  title: string;
  description: string;
  params?: { name: string; type: string; required: boolean; description: string }[];
  body?: { name: string; type: string; required: boolean; description: string }[];
  responseExample: string;
  curl: string;
};

const endpoints: Endpoint[] = [
  {
    method: "GET",
    path: "/api/health",
    title: "Status da API",
    description: "Verifica se a API esta online e retorna informacoes basicas do sistema.",
    responseExample: JSON.stringify({ status: "ok", timestamp: "2026-03-07T12:00:00.000Z", version: "1.0.0", database: "connected" }, null, 2),
    curl: "curl {BASE}/api/health",
  },
  {
    method: "GET",
    path: "/api/stats",
    title: "Estatisticas Gerais",
    description: "Retorna contagem total de registros, distribuicao por tipo de entidade e ranking dos 10 estados com mais cadastros.",
    responseExample: JSON.stringify({
      totalEntities: 113002,
      byType: [{ entityType: "cadastro", count: "113001" }],
      topStates: [{ state: "MG", count: "20893" }, { state: "GO", count: "13863" }]
    }, null, 2),
    curl: "curl {BASE}/api/stats",
  },
  {
    method: "GET",
    path: "/api/entities",
    title: "Listar Cadastros",
    description: "Retorna uma lista paginada de cadastros com suporte a filtros por tipo, estado, cidade, categoria e busca textual por nome, CPF/CNPJ, codigo ou email.",
    params: [
      { name: "limit", type: "number", required: false, description: "Quantidade de registros por pagina (padrao: 100, max: 1000)" },
      { name: "offset", type: "number", required: false, description: "Deslocamento para paginacao (padrao: 0)" },
      { name: "search", type: "string", required: false, description: "Busca por nome, CPF/CNPJ, codigo ou email" },
      { name: "entityType", type: "string", required: false, description: "Filtrar por tipo (ex: cadastro, cliente, parceiro)" },
      { name: "state", type: "string", required: false, description: "Filtrar por UF (ex: SP, MG, GO)" },
      { name: "city", type: "string", required: false, description: "Filtrar por cidade (ex: Sao Paulo)" },
      { name: "category", type: "string", required: false, description: "Filtrar por categoria" },
    ],
    responseExample: JSON.stringify({
      data: [{
        id: 1, entityType: "cadastro", name: "Joao da Silva",
        cpfCnpj: "12345678901", state: "SP", city: "Sao Paulo",
        phone: "11999998888", email: "joao@email.com",
        category: "Pecuarista", segment: "Empresario"
      }],
      total: 113002, limit: 100, offset: 0
    }, null, 2),
    curl: 'curl "{BASE}/api/entities?state=SP&limit=10&offset=0"',
  },
  {
    method: "GET",
    path: "/api/entities/:id",
    title: "Detalhe do Cadastro",
    description: "Retorna todos os dados de um cadastro especifico pelo ID, incluindo enderecos, contatos, segmentos e dados adicionais.",
    params: [
      { name: "id", type: "number", required: true, description: "ID unico do cadastro" },
    ],
    responseExample: JSON.stringify({
      id: 1, entityType: "cadastro", name: "Joao da Silva",
      cpfCnpj: "12345678901", code: "A001",
      state: "SP", city: "Sao Paulo", region: "Sudeste",
      address: "Rua das Flores, 123", neighborhood: "Centro", cep: "01001000",
      phone: "1133334444", cellphone: "11999998888",
      extraPhones: ["11988887777"], email: "joao@email.com",
      category: "Pecuarista", subCategory: "Bovinos",
      segment: "Empresario", segment2: "Grande Porte",
      birthDate: "15/03/1980",
      deliveryAddress: "Av. Brasil, 456", deliveryNeighborhood: "Vila Nova",
      deliveryCity: "Sao Paulo", deliveryState: "SP", deliveryCep: "02002000",
      additionalData: {}, sourceFile: "base_2024.xlsx"
    }, null, 2),
    curl: "curl {BASE}/api/entities/1",
  },
  {
    method: "GET",
    path: "/api/entities/search/cpf/:cpfCnpj",
    title: "Buscar por CPF/CNPJ",
    description: "Busca direta por CPF ou CNPJ. Retorna o cadastro correspondente ou 404 se nao encontrado. Util para validacao rapida de duplicidade.",
    params: [
      { name: "cpfCnpj", type: "string", required: true, description: "CPF (11 digitos) ou CNPJ (14 digitos)" },
    ],
    responseExample: JSON.stringify({
      id: 1, entityType: "cadastro", name: "Joao da Silva",
      cpfCnpj: "12345678901", state: "SP", city: "Sao Paulo"
    }, null, 2),
    curl: "curl {BASE}/api/entities/search/cpf/12345678901",
  },
  {
    method: "GET",
    path: "/api/entities/types",
    title: "Listar Tipos",
    description: "Retorna todos os tipos de entidade distintos presentes no banco de dados.",
    responseExample: JSON.stringify(["cadastro", "cliente", "parceiro"], null, 2),
    curl: "curl {BASE}/api/entities/types",
  },
  {
    method: "GET",
    path: "/api/entities/states",
    title: "Listar Estados (UFs)",
    description: "Retorna todas as UFs distintas presentes no banco de dados, ordenadas alfabeticamente.",
    responseExample: JSON.stringify(["AC", "AL", "AM", "AP", "BA", "CE", "DF", "ES", "GO", "MG", "SP"], null, 2),
    curl: "curl {BASE}/api/entities/states",
  },
  {
    method: "GET",
    path: "/api/entities/categories",
    title: "Listar Categorias",
    description: "Retorna todas as categorias distintas presentes no banco de dados.",
    responseExample: JSON.stringify(["Pecuarista", "Empresario", "Autonomo", "Medico/Dentista"], null, 2),
    curl: "curl {BASE}/api/entities/categories",
  },
  {
    method: "GET",
    path: "/api/entities/segments",
    title: "Listar Segmentos",
    description: "Retorna todos os segmentos distintos presentes no banco de dados.",
    responseExample: JSON.stringify(["Pecuarista", "Empresario", "Autonomo", "Funcionario Publico"], null, 2),
    curl: "curl {BASE}/api/entities/segments",
  },
  {
    method: "GET",
    path: "/api/entities/cities/:state",
    title: "Listar Cidades por Estado",
    description: "Retorna todas as cidades distintas de um estado especifico.",
    params: [
      { name: "state", type: "string", required: true, description: "UF do estado (ex: SP, MG, GO)" },
    ],
    responseExample: JSON.stringify(["Sao Paulo", "Campinas", "Santos", "Ribeirao Preto"], null, 2),
    curl: "curl {BASE}/api/entities/cities/SP",
  },
  {
    method: "POST",
    path: "/api/entities",
    title: "Criar Cadastro",
    description: "Cria um novo cadastro no sistema. Se o CPF/CNPJ ja existir, retorna erro 409 (conflito).",
    body: [
      { name: "name", type: "string", required: true, description: "Nome completo da pessoa ou empresa" },
      { name: "entityType", type: "string", required: true, description: "Tipo: cadastro, cliente, parceiro, outro" },
      { name: "cpfCnpj", type: "string", required: false, description: "CPF (11 digitos) ou CNPJ (14 digitos)" },
      { name: "state", type: "string", required: false, description: "UF (ex: SP)" },
      { name: "city", type: "string", required: false, description: "Cidade" },
      { name: "phone", type: "string", required: false, description: "Telefone fixo" },
      { name: "cellphone", type: "string", required: false, description: "Celular" },
      { name: "email", type: "string", required: false, description: "Email de contato" },
      { name: "address", type: "string", required: false, description: "Endereco" },
      { name: "neighborhood", type: "string", required: false, description: "Bairro" },
      { name: "cep", type: "string", required: false, description: "CEP (8 digitos)" },
      { name: "category", type: "string", required: false, description: "Categoria principal" },
      { name: "segment", type: "string", required: false, description: "Segmento" },
      { name: "birthDate", type: "string", required: false, description: "Data de nascimento (dd/mm/aaaa)" },
    ],
    responseExample: JSON.stringify({
      id: 113003, entityType: "cadastro", name: "Maria Santos",
      cpfCnpj: "98765432100", state: "RJ", city: "Rio de Janeiro"
    }, null, 2),
    curl: `curl -X POST {BASE}/api/entities \\
  -H "Content-Type: application/json" \\
  -d '{"name":"Maria Santos","entityType":"cadastro","cpfCnpj":"98765432100","state":"RJ","city":"Rio de Janeiro"}'`,
  },
  {
    method: "PUT",
    path: "/api/entities/:id",
    title: "Atualizar Cadastro",
    description: "Atualiza parcialmente um cadastro existente. Envie apenas os campos que deseja alterar.",
    params: [
      { name: "id", type: "number", required: true, description: "ID unico do cadastro" },
    ],
    body: [
      { name: "name", type: "string", required: false, description: "Nome completo" },
      { name: "phone", type: "string", required: false, description: "Telefone" },
      { name: "email", type: "string", required: false, description: "Email" },
      { name: "city", type: "string", required: false, description: "Cidade" },
      { name: "state", type: "string", required: false, description: "UF" },
    ],
    responseExample: JSON.stringify({
      id: 1, entityType: "cadastro", name: "Joao da Silva Atualizado",
      updatedAt: "2026-03-07T12:00:00.000Z"
    }, null, 2),
    curl: `curl -X PUT {BASE}/api/entities/1 \\
  -H "Content-Type: application/json" \\
  -d '{"name":"Joao da Silva Atualizado","phone":"11999990000"}'`,
  },
  {
    method: "DELETE",
    path: "/api/entities/:id",
    title: "Remover Cadastro",
    description: "Remove permanentemente um cadastro do sistema. Retorna 204 (sem conteudo) em caso de sucesso.",
    params: [
      { name: "id", type: "number", required: true, description: "ID unico do cadastro" },
    ],
    responseExample: "(Sem corpo - HTTP 204 No Content)",
    curl: "curl -X DELETE {BASE}/api/entities/1",
  },
  {
    method: "POST",
    path: "/api/upload/preview",
    title: "Pre-visualizar Planilha",
    description: "Envia um arquivo Excel (.xlsx/.xls) e retorna os nomes das colunas e as primeiras 5 linhas para pre-visualizacao antes da importacao.",
    body: [
      { name: "file", type: "file", required: true, description: "Arquivo Excel (multipart/form-data)" },
    ],
    responseExample: JSON.stringify({
      fileName: "base_2024.xlsx",
      totalRows: 5000,
      columns: ["NOME", "CPF_CNPJ", "CIDADE", "UF"],
      preview: [{ NOME: "Joao", CPF_CNPJ: "12345678901", CIDADE: "Sao Paulo", UF: "SP" }]
    }, null, 2),
    curl: `curl -X POST {BASE}/api/upload/preview \\
  -F "file=@planilha.xlsx"`,
  },
  {
    method: "POST",
    path: "/api/upload/import",
    title: "Importar Planilha",
    description: "Importa registros de um arquivo Excel com mapeamento de colunas personalizado. Detecta duplicatas por CPF/CNPJ automaticamente.",
    body: [
      { name: "file", type: "file", required: true, description: "Arquivo Excel (multipart/form-data)" },
      { name: "mapping", type: "JSON string", required: true, description: "Mapeamento coluna Excel -> campo do banco (ex: {\"NOME\":\"name\",\"CPF\":\"cpfCnpj\"})" },
      { name: "entityType", type: "string", required: true, description: "Tipo de entidade para os registros importados" },
    ],
    responseExample: JSON.stringify({
      success: true, imported: 4850, total: 5000,
      errors: [{ row: 15, error: "CPF/CNPJ duplicado: 12345678901" }],
      fileUploadId: 1
    }, null, 2),
    curl: `curl -X POST {BASE}/api/upload/import \\
  -F "file=@planilha.xlsx" \\
  -F 'mapping={"NOME":"name","CPF":"cpfCnpj","UF":"state"}' \\
  -F "entityType=cadastro"`,
  },
  {
    method: "GET",
    path: "/api/uploads",
    title: "Historico de Uploads",
    description: "Retorna o historico dos ultimos 50 uploads de planilhas com status e quantidade de registros importados.",
    responseExample: JSON.stringify([{
      id: 1, fileName: "base_2024.xlsx", fileSize: 5242880,
      recordsImported: 4850, status: "completed",
      uploadedAt: "2026-03-07T10:00:00.000Z"
    }], null, 2),
    curl: "curl {BASE}/api/uploads",
  },
];

const moduleEndpoints: Endpoint[] = [
  {
    method: "GET",
    path: "/api/anm/import/status",
    title: "Status ANM Worker",
    description: "Retorna o status atual do worker de importação ANM (processos, barragens, enriquecimento).",
    responseExample: JSON.stringify({ worker: { phase: "idle" }, database: { anm_processos: 45000, anm_barragens: 900 } }, null, 2),
    curl: 'curl "{BASE}/api/anm/import/status"',
  },
  {
    method: "GET",
    path: "/api/anm/processos",
    title: "Listar Processos Minerários",
    description: "Lista processos ANM com filtros por UF, fase, substância, uso e busca textual. Suporta paginação.",
    params: [
      { name: "uf", type: "string", required: false, description: "Filtrar por UF (ex: MG, PA)" },
      { name: "fase", type: "string", required: false, description: "Fase do processo (ex: CONCESSÃO DE LAVRA)" },
      { name: "subs", type: "string", required: false, description: "Substância mineral" },
      { name: "limit", type: "number", required: false, description: "Limite de registros (padrão: 20)" },
      { name: "offset", type: "number", required: false, description: "Offset para paginação" },
    ],
    responseExample: JSON.stringify({ data: [{ numero: "830.001/2020", nome: "MINERADORA X", uf: "MG", fase: "AUTORIZAÇÃO DE PESQUISA", area_ha: 500 }], total: 45000 }, null, 2),
    curl: 'curl "{BASE}/api/anm/processos?uf=MG&limit=10"',
  },
  {
    method: "GET",
    path: "/api/sicar/imoveis",
    title: "Listar Imóveis SICAR",
    description: "Lista imóveis rurais do SICAR com filtros por UF, município e paginação.",
    params: [
      { name: "uf", type: "string", required: false, description: "Filtrar por UF" },
      { name: "municipio", type: "string", required: false, description: "Nome do município" },
      { name: "limit", type: "number", required: false, description: "Limite (padrão: 50)" },
      { name: "offset", type: "number", required: false, description: "Offset para paginação" },
    ],
    responseExample: JSON.stringify({ data: [{ cod_imovel: "GO-5208004-abc123", municipio: "Goiânia", uf: "GO", area_ha: 150 }], total: 2100000 }, null, 2),
    curl: 'curl "{BASE}/api/sicar/imoveis?uf=GO&limit=5"',
  },
  {
    method: "GET",
    path: "/api/sicar/stats",
    title: "Estatísticas SICAR",
    description: "Retorna contagem total de imóveis, distribuição por UF e status de enriquecimento.",
    responseExample: JSON.stringify({ total: 2100000, por_uf: [{ uf: "MG", count: 350000 }] }, null, 2),
    curl: 'curl "{BASE}/api/sicar/stats"',
  },
  {
    method: "GET",
    path: "/api/ibama/autuacoes/status",
    title: "Status IBAMA Autuações",
    description: "Retorna contagem e data da última importação de autuações IBAMA.",
    responseExample: JSON.stringify({ total: 150000, ultima_importacao: "2026-03-01T00:00:00Z" }, null, 2),
    curl: 'curl "{BASE}/api/ibama/autuacoes/status"',
  },
  {
    method: "GET",
    path: "/api/icmbio/status",
    title: "Status ICMBio",
    description: "Retorna contagem de Unidades de Conservação e distribuição por grupo (Proteção Integral / Uso Sustentável).",
    responseExample: JSON.stringify({ total: 344, por_grupo: [{ grupo: "PROTECAO_INTEGRAL", count: 180 }] }, null, 2),
    curl: 'curl "{BASE}/api/icmbio/status"',
  },
  {
    method: "GET",
    path: "/api/pgfn/status",
    title: "Status PGFN",
    description: "Retorna contagem total de inscrições na Dívida Ativa, distribuição por tipo e por UF.",
    responseExample: JSON.stringify({ total: 500000, por_tipo: { fgts: 150000, previdenciario: 200000, nao_previdenciario: 150000 } }, null, 2),
    curl: 'curl "{BASE}/api/pgfn/status"',
  },
  {
    method: "GET",
    path: "/api/funai/terras",
    title: "Listar Terras Indígenas",
    description: "Lista Terras Indígenas com filtros por UF e fase de demarcação.",
    params: [
      { name: "uf", type: "string", required: false, description: "Filtrar por UF" },
      { name: "fase", type: "string", required: false, description: "Fase (Regularizada, Homologada, etc.)" },
    ],
    responseExample: JSON.stringify({ data: [{ nome: "Xavante", uf: "MT", fase: "Regularizada", area_ha: 250000 }], total: 680 }, null, 2),
    curl: 'curl "{BASE}/api/funai/terras?uf=MT"',
  },
  {
    method: "GET",
    path: "/api/agrofit/produtos",
    title: "Listar Produtos AGROFIT",
    description: "Lista produtos agrotóxicos com filtros por classe, status, cultura e ingrediente ativo.",
    params: [
      { name: "search", type: "string", required: false, description: "Busca por nome ou marca" },
      { name: "classe", type: "string", required: false, description: "Classe toxicológica" },
      { name: "status", type: "string", required: false, description: "ativo ou cancelado" },
      { name: "limit", type: "number", required: false, description: "Limite (padrão: 20)" },
    ],
    responseExample: JSON.stringify({ data: [{ id_agrofit: 1, marca_comercial: "ROUNDUP", titular_registro: "BAYER" }], total: 5000 }, null, 2),
    curl: 'curl "{BASE}/api/agrofit/produtos?search=roundup&limit=5"',
  },
  {
    method: "GET",
    path: "/api/sgb/status",
    title: "Status SGB",
    description: "Retorna contagem de bacias sedimentares e dados de geocronologia.",
    responseExample: JSON.stringify({ tabelas: { sgb_bacias_sedimentares: { total: 600 }, sgb_geocronologia: { total: 3000 } } }, null, 2),
    curl: 'curl "{BASE}/api/sgb/status"',
  },
  {
    method: "GET",
    path: "/api/cprm/geologia",
    title: "Consulta Geológica CPRM",
    description: "Consulta ponto em polígono no mapa geológico 1:2.500.000.",
    params: [
      { name: "lat", type: "number", required: true, description: "Latitude" },
      { name: "lon", type: "number", required: true, description: "Longitude" },
    ],
    responseExample: JSON.stringify({ sigla: "NP3βt", nome: "Grupo Tocantins", hierarquia: "Neoproterozoico" }, null, 2),
    curl: 'curl "{BASE}/api/cprm/geologia?lat=-15.78&lon=-47.93"',
  },
  {
    method: "GET",
    path: "/api/health/modules",
    title: "Saúde dos Módulos",
    description: "Retorna status de todos os módulos do sistema: contagem de registros e data da última importação.",
    responseExample: JSON.stringify({ modules: [{ name: "anm", label: "ANM SIGMINE", total: 45000, last_import: "2026-03-01" }] }, null, 2),
    curl: 'curl "{BASE}/api/health/modules"',
  },
  {
    method: "GET",
    path: "/api/scheduler/modules",
    title: "Agendamentos de Módulos",
    description: "Lista todos os agendamentos de importação/sync de módulos (cron, status, próxima execução).",
    responseExample: JSON.stringify([{ id: 1, modulo: "ibama_autuacoes", cron_expression: "0 3 * * 1", ativo: true }], null, 2),
    curl: 'curl "{BASE}/api/scheduler/modules"',
  },
];

const methodColors: Record<string, string> = {
  GET: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  POST: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  PUT: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  DELETE: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 px-2"
      data-testid="button-copy-curl"
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
    </Button>
  );
}

function EndpointCard({ endpoint }: { endpoint: Endpoint }) {
  const [expanded, setExpanded] = useState(false);
  const curlWithBase = endpoint.curl.replace(/\{BASE\}/g, BASE_URL);

  return (
    <Card className="overflow-hidden" data-testid={`card-endpoint-${endpoint.method.toLowerCase()}-${endpoint.path.replace(/[/:]/g, "-")}`}>
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
        data-testid={`button-expand-${endpoint.path.replace(/[/:]/g, "-")}`}
      >
        {expanded ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
        <Badge className={`${methodColors[endpoint.method]} font-mono text-xs px-2 py-0.5 shrink-0`}>
          {endpoint.method}
        </Badge>
        <code className="text-sm font-mono font-medium flex-1">{endpoint.path}</code>
        <span className="text-sm text-muted-foreground hidden sm:block">{endpoint.title}</span>
      </div>

      {expanded && (
        <div className="border-t px-4 py-4 space-y-4 bg-muted/10">
          <p className="text-sm text-muted-foreground">{endpoint.description}</p>

          {endpoint.params && endpoint.params.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-2">Parametros</h4>
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Nome</th>
                      <th className="text-left px-3 py-2 font-medium">Tipo</th>
                      <th className="text-left px-3 py-2 font-medium hidden sm:table-cell">Obrigatorio</th>
                      <th className="text-left px-3 py-2 font-medium">Descricao</th>
                    </tr>
                  </thead>
                  <tbody>
                    {endpoint.params.map((p) => (
                      <tr key={p.name} className="border-t">
                        <td className="px-3 py-2 font-mono text-xs">{p.name}</td>
                        <td className="px-3 py-2 text-muted-foreground">{p.type}</td>
                        <td className="px-3 py-2 hidden sm:table-cell">
                          {p.required ? (
                            <Badge variant="destructive" className="text-xs">Sim</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">Nao</Badge>
                          )}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{p.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {endpoint.body && endpoint.body.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-2">Corpo da Requisicao (JSON)</h4>
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Campo</th>
                      <th className="text-left px-3 py-2 font-medium">Tipo</th>
                      <th className="text-left px-3 py-2 font-medium hidden sm:table-cell">Obrigatorio</th>
                      <th className="text-left px-3 py-2 font-medium">Descricao</th>
                    </tr>
                  </thead>
                  <tbody>
                    {endpoint.body.map((b) => (
                      <tr key={b.name} className="border-t">
                        <td className="px-3 py-2 font-mono text-xs">{b.name}</td>
                        <td className="px-3 py-2 text-muted-foreground">{b.type}</td>
                        <td className="px-3 py-2 hidden sm:table-cell">
                          {b.required ? (
                            <Badge variant="destructive" className="text-xs">Sim</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">Nao</Badge>
                          )}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{b.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold">Exemplo de Resposta</h4>
            </div>
            <pre className="bg-foreground/5 dark:bg-foreground/10 rounded-lg p-3 text-xs overflow-x-auto font-mono leading-relaxed">
              {endpoint.responseExample}
            </pre>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold">cURL</h4>
              <CopyButton text={curlWithBase} />
            </div>
            <pre className="bg-foreground/5 dark:bg-foreground/10 rounded-lg p-3 text-xs overflow-x-auto font-mono leading-relaxed whitespace-pre-wrap break-all">
              {curlWithBase}
            </pre>
          </div>
        </div>
      )}
    </Card>
  );
}

function TryItPanel() {
  const [url, setUrl] = useState("/api/entities?limit=3");
  const [response, setResponse] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const tryRequest = async () => {
    setLoading(true);
    setResponse(null);
    try {
      const res = await fetch(url);
      const data = await res.json();
      setResponse(JSON.stringify(data, null, 2));
    } catch (e: any) {
      setResponse(`Erro: ${e.message}`);
    }
    setLoading(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Play className="w-5 h-5" />
          Testar Endpoint
        </CardTitle>
        <CardDescription>Faca uma requisicao GET direto do navegador</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Badge className={`${methodColors.GET} shrink-0 self-center`}>GET</Badge>
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="/api/entities?limit=5"
            className="font-mono text-sm"
            data-testid="input-try-url"
          />
          <Button onClick={tryRequest} disabled={loading} data-testid="button-try-send">
            {loading ? "..." : "Enviar"}
          </Button>
        </div>
        {response && (
          <pre className="bg-foreground/5 dark:bg-foreground/10 rounded-lg p-3 text-xs overflow-x-auto font-mono leading-relaxed max-h-96 overflow-y-auto" data-testid="text-try-response">
            {response}
          </pre>
        )}
      </CardContent>
    </Card>
  );
}

export default function ApiDocsPage() {
  const cadastroSections = [
    { title: "Sistema", endpoints: endpoints.filter(e => ["/api/health", "/api/stats"].includes(e.path)) },
    { title: "Consultas", endpoints: endpoints.filter(e => e.method === "GET" && e.path.startsWith("/api/entities") && !e.path.includes("upload")) },
    { title: "Cadastro", endpoints: endpoints.filter(e => (e.method === "POST" || e.method === "PUT" || e.method === "DELETE") && e.path.startsWith("/api/entities")) },
    { title: "Upload / Importacao", endpoints: endpoints.filter(e => e.path.includes("upload")) },
  ];

  const moduleSections = [
    { title: "Mineracao", icon: Pickaxe, color: "text-amber-600", endpoints: moduleEndpoints.filter(e => e.path.includes("/anm/") || e.path.includes("/cprm/") || e.path.includes("/sgb/")) },
    { title: "Agro & Fundiario", icon: Leaf, color: "text-green-600", endpoints: moduleEndpoints.filter(e => e.path.includes("/sicar/") || e.path.includes("/agrofit/")) },
    { title: "Ambiental", icon: Trees, color: "text-green-700", endpoints: moduleEndpoints.filter(e => e.path.includes("/ibama/") || e.path.includes("/icmbio/") || e.path.includes("/funai/")) },
    { title: "Fiscal", icon: Landmark, color: "text-blue-700", endpoints: moduleEndpoints.filter(e => e.path.includes("/pgfn/")) },
    { title: "Sistema", icon: Server, color: "text-gray-600", endpoints: moduleEndpoints.filter(e => e.path.includes("/health/") || e.path.includes("/scheduler/")) },
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <ModuleHeader
        icon={BookOpen}
        iconColor="text-indigo-600"
        title="Documentação da API"
        description="Referência completa dos endpoints REST — Mavrion Hub API"
      />

      <div className="grid sm:grid-cols-3 gap-4">
        <Card className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
          <CardContent className="pt-5 flex items-start gap-3">
            <Server className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-sm">Base URL</p>
              <code className="text-xs text-muted-foreground break-all">{BASE_URL}</code>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800">
          <CardContent className="pt-5 flex items-start gap-3">
            <Zap className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-sm">Formato</p>
              <code className="text-xs text-muted-foreground">JSON (application/json)</code>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800">
          <CardContent className="pt-5 flex items-start gap-3">
            <Shield className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-sm">Autenticacao</p>
              <code className="text-xs text-muted-foreground">Sem autenticacao (v1)</code>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Codigos de Resposta HTTP</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">200</Badge>
              <span className="text-muted-foreground">Sucesso</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">201</Badge>
              <span className="text-muted-foreground">Criado</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">204</Badge>
              <span className="text-muted-foreground">Sem conteudo</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">400</Badge>
              <span className="text-muted-foreground">Requisicao invalida</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">404</Badge>
              <span className="text-muted-foreground">Nao encontrado</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">409</Badge>
              <span className="text-muted-foreground">CPF/CNPJ duplicado</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">500</Badge>
              <span className="text-muted-foreground">Erro interno</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Modelo de Dados — Cadastro</CardTitle>
          <CardDescription>Campos retornados para cada registro</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Campo</th>
                  <th className="text-left px-3 py-2 font-medium">Tipo</th>
                  <th className="text-left px-3 py-2 font-medium">Descricao</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["id", "integer", "Identificador unico"],
                  ["entityType", "string", "Tipo (cadastro, cliente, parceiro, outro)"],
                  ["name", "string", "Nome completo"],
                  ["cpfCnpj", "string", "CPF ou CNPJ"],
                  ["code", "string", "Codigo interno"],
                  ["state", "string", "UF (2 letras)"],
                  ["city", "string", "Cidade"],
                  ["region", "string", "Regiao"],
                  ["address", "string", "Endereco principal"],
                  ["neighborhood", "string", "Bairro"],
                  ["cep", "string", "CEP"],
                  ["phone", "string", "Telefone fixo"],
                  ["cellphone", "string", "Celular"],
                  ["extraPhones", "string[]", "Telefones adicionais"],
                  ["email", "string", "Email de contato"],
                  ["category", "string", "Categoria principal"],
                  ["subCategory", "string", "Subcategoria"],
                  ["productType", "string", "Tipo de produto"],
                  ["segment", "string", "Segmento primario"],
                  ["segment2", "string", "Segmento secundario"],
                  ["birthDate", "string", "Data de nascimento (dd/mm/aaaa)"],
                  ["deliveryAddress", "string", "Endereco de entrega"],
                  ["deliveryNeighborhood", "string", "Bairro de entrega"],
                  ["deliveryCity", "string", "Cidade de entrega"],
                  ["deliveryState", "string", "UF de entrega"],
                  ["deliveryCep", "string", "CEP de entrega"],
                  ["additionalData", "object", "Campos extras (JSONB)"],
                  ["sourceFile", "string", "Arquivo de origem"],
                  ["importedAt", "datetime", "Data/hora da importacao"],
                  ["createdAt", "datetime", "Data/hora de criacao"],
                  ["updatedAt", "datetime", "Data/hora da ultima atualizacao"],
                ].map(([field, type, desc]) => (
                  <tr key={field} className="border-t">
                    <td className="px-3 py-1.5 font-mono text-xs">{field}</td>
                    <td className="px-3 py-1.5 text-muted-foreground text-xs">{type}</td>
                    <td className="px-3 py-1.5 text-muted-foreground text-xs">{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <h2 className="text-xl font-bold border-b pb-2 pt-4" data-testid="text-section-cadastro-header">Cadastro de Entidades</h2>
      {cadastroSections.map((section) => (
        <div key={section.title} className="space-y-3">
          <h3 className="text-lg font-semibold border-b pb-2" data-testid={`text-section-${section.title.toLowerCase()}`}>{section.title}</h3>
          {section.endpoints.map((ep) => (
            <EndpointCard key={`${ep.method}-${ep.path}`} endpoint={ep} />
          ))}
        </div>
      ))}

      <h2 className="text-xl font-bold border-b pb-2 pt-4" data-testid="text-section-modules-header">Módulos Especializados</h2>
      {moduleSections.map((section) => {
        const SectionIcon = section.icon;
        return (
          <div key={section.title} className="space-y-3">
            <h3 className="text-lg font-semibold border-b pb-2 flex items-center gap-2" data-testid={`text-section-${section.title.toLowerCase()}`}>
              <SectionIcon className={`h-5 w-5 ${section.color}`} />
              {section.title}
            </h3>
            {section.endpoints.map((ep) => (
              <EndpointCard key={`${ep.method}-${ep.path}`} endpoint={ep} />
            ))}
          </div>
        );
      })}

      <h2 className="text-xl font-bold border-b pb-2 pt-4" data-testid="text-section-external-header">Fontes de Dados Externas</h2>
      <p className="text-sm text-muted-foreground">APIs e serviços oficiais consumidos pelo Mavrion Hub para alimentar cada módulo.</p>

      <div className="grid sm:grid-cols-2 gap-4">
        {[
          { vertical: "Mineração", color: "border-amber-300 dark:border-amber-700", items: [
            { name: "ANM SIGMINE", url: "https://geo.anm.gov.br/arcgis/rest/services", method: "ArcGIS REST", desc: "Polígonos de processos minerários por UF e fase" },
            { name: "ANM SIGBM", url: "https://app.anm.gov.br/SIGBM/Publico/GerenciarPublico", method: "CSV", desc: "Barragens de mineração (download direto)" },
            { name: "SGB GeoServer", url: "https://geoservicos.sgb.gov.br/geoserver", method: "WFS", desc: "Bacias sedimentares e geocronologia" },
            { name: "CPRM Geoportal", url: "https://geoportal.sgb.gov.br/server/rest/services", method: "ArcGIS REST", desc: "Mapa litoestratigráfico 1:2.500.000" },
          ]},
          { vertical: "Agro & Fundiário", color: "border-green-300 dark:border-green-700", items: [
            { name: "SICAR / CAR", url: "https://consultapublica.car.gov.br/publico", method: "WFS", desc: "Imóveis rurais por UF (geometria + atributos)" },
            { name: "AGROFIT", url: "https://agrofit.agricultura.gov.br", method: "Web Scraping", desc: "Produtos agrotóxicos, ingredientes ativos, indicações" },
            { name: "OpenGeoHub S3", url: "https://s3.opengeohub.org", method: "COG (GeoTIFF)", desc: "Argila ISO 11277 — 3 profundidades, 120m resolução" },
          ]},
          { vertical: "Ambiental", color: "border-emerald-300 dark:border-emerald-700", items: [
            { name: "IBAMA Dados Abertos", url: "https://dadosabertos.ibama.gov.br", method: "ZIP/CSV", desc: "Autuações ambientais e embargos" },
            { name: "ICMBio / INDE", url: "https://geoservicos.inde.gov.br/geoserver", method: "WFS", desc: "Unidades de Conservação (CNUC)" },
            { name: "FUNAI GeoServer", url: "https://geoserver.funai.gov.br/geoserver", method: "WFS", desc: "Terras Indígenas homologadas e em estudo" },
          ]},
          { vertical: "Fiscal", color: "border-blue-300 dark:border-blue-700", items: [
            { name: "PGFN Dados Abertos", url: "https://dadosabertos.pgfn.gov.br", method: "ZIP/CSV", desc: "Dívida Ativa da União (FGTS, Previdenciário, Não-Previdenciário)" },
          ]},
        ].map((group) => (
          <Card key={group.vertical} className={`${group.color} border-l-4`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{group.vertical}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {group.items.map((item) => (
                <div key={item.name} className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{item.name}</span>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">{item.method}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                  <a href={item.url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1">
                    <ExternalLink className="h-3 w-3" /> {item.url}
                  </a>
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>

      <TryItPanel />
    </div>
  );
}
